import { createContext, useContext, useEffect, useRef, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeAnimatedHeight } from "./RyeAnimatedHeight";
import { RyeButton } from "./RyeButton";
import { keyCodes } from "../utils/KeyCodes";
import { useKeyDown } from "../utils/customHooks";
import React from "react";
import { RyeVariant, RyeVibe } from "./RyeConsts";
import {
  RYE_MODAL_EXIT_MS,
  RyeModalPhase,
  RyeModalBackdrop,
  ryeModalPanelClasses,
} from "./RyeModalMotion";

export type ActionButton = {
  text: string;
  variant?: RyeVariant;
  vibe?: RyeVibe;
  disabled?: boolean;
  close?: boolean;
  action: () => void;
};

type RyeModalContextType = {
  openModal: (content: React.ReactNode) => void;
  closeModal: () => void;
  /** Lets layers beneath the modal (e.g. Settings) leave keys like Esc to it. */
  isOpen: boolean;
};

const RyeModalContext = createContext<RyeModalContextType>({
  openModal: () => {},
  closeModal: () => {},
  isOpen: false,
});

export function useRyeModal(): RyeModalContextType {
  return useContext(RyeModalContext);
}

export function RyeModalAnchor(props: { children: JSX.Element }) {
  // State variables set by RyeModalContextType.openModal parameters
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [content, setContent] = useState<React.ReactNode>(null);

  const [phase, setPhase] = useState<RyeModalPhase>("entering");
  const modalRef = useRef<HTMLDivElement>(null);

  function getContent(): React.ReactNode {
    if (!isOpen) {
      return null;
    }
    return (
      <div
        className={buildClasses(
          "fixed",
          "inset-0",
          "z-[1000000000]",
          "w-screen",
          "h-screen",
          "flex",
          "items-center",
          "justify-center",
        )}
      >
        <RyeModalBackdrop phase={phase} dimClass="bg-on-surface-900/10" />
        <div
          ref={modalRef}
          className={buildClasses(
            ryeModalPanelClasses(phase),
            "shadow-modal",
            "bg-surface-50",
            "rounded-xl",
            "m-3",
            "p-8",
            "flex-shrink",
            "z-0",
          )}
          tabIndex={0}
        >
          {content}
        </div>
      </div>
    );
  }

  function close() {
    setIsOpen(false);
  }

  function openModal(content: React.ReactNode) {
    setIsOpen(true);
    setContent(content);
  }

  function closeModal() {
    setPhase("closing");
    setTimeout(() => close(), RYE_MODAL_EXIT_MS);
  }

  useEffect(() => {
    if (!isOpen) {
      setPhase("entering");
      return;
    }
    // Content that focuses something itself (e.g. a search box) has already
    // done so by now; only take focus if it didn't.
    if (!modalRef.current?.contains(document.activeElement)) {
      modalRef.current?.focus();
    }
    // Wait a frame so the entering styles have painted, giving the opening
    // animation a start state to transition from.
    const frame = requestAnimationFrame(() => setPhase("open"));
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  return (
    <RyeModalContext.Provider
      value={{ openModal: openModal, closeModal: closeModal, isOpen: isOpen }}
    >
      {getContent()}
      {props.children}
    </RyeModalContext.Provider>
  );
}

export function RyeModalContent({
  content,
  actionButtons,
  title,
  showCloseX = true, // note: in general it is considered good UX to always include the `x` in the top-right, see https://ux.stackexchange.com/questions/84854/does-confirmation-dialog-need-a-close-x-button
  alignActionButtons = "end",
  animateHeight = false,
  onClose = () => {},
  onCloseX = () => {},
}: {
  content: React.ReactNode;
  actionButtons: ActionButton[];
  title?: React.ReactNode;
  animateHeight?: boolean;
  showCloseX?: boolean;
  alignActionButtons?: "start" | "center" | "between" | "end";
  onClose?: () => void;
  onCloseX?: () => void;
}) {
  const { closeModal } = useRyeModal();

  function closeDefault() {
    closeModal();
    onClose();
  }

  function closeX() {
    closeDefault();
    onCloseX();
  }

  const body = (
    <React.Fragment>
      <Header title={title} showCloseX={showCloseX} close={closeX} />
      <div
        className={buildClasses(
          { if: !animateHeight || !!title || showCloseX, then: "mt-7" },
          { if: !animateHeight || actionButtons.length > 0, then: "mb-8" },
        )}
      >
        {content}
      </div>
      <Footer
        actionButtons={actionButtons}
        alignActionButtons={alignActionButtons}
        close={closeDefault}
      />
    </React.Fragment>
  );
  return animateHeight ? <RyeAnimatedHeight>{body}</RyeAnimatedHeight> : body;
}

function Header({
  showCloseX,
  close,
  title,
}: {
  showCloseX: boolean;
  close: () => void;
  title?: React.ReactNode;
}): JSX.Element {
  const headerRef = useRef<HTMLDivElement>(null);

  useKeyDown(headerRef, showCloseX, handleKeyDown);

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.code === keyCodes.ESC) {
      close();
    }
  }

  if (!title && !showCloseX) {
    return <div />;
  }

  return (
    <div
      ref={headerRef}
      className={buildClasses(
        "flex",
        "items-center",
        "justify-between",
        "text-lg",
        "font-medium",
      )}
    >
      <div className={buildClasses("pr-10")}>{title}</div>
      {showCloseX && (
        <RyeButton
          icon="close"
          variant="transparent"
          vibe="subdued"
          onClick={close}
        />
      )}
    </div>
  );
}

function Footer({
  actionButtons,
  alignActionButtons,
  close,
}: {
  actionButtons: ActionButton[];
  alignActionButtons?: "start" | "center" | "between" | "end";
  close: () => void;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        {
          switch: alignActionButtons,
          cases: new Map([
            ["start", "justify-start"],
            ["center", "justify-center"],
            ["between", "justify-between"],
            ["end", "justify-end"],
          ]),
        },
        "flex",
        "items-center",
        "gap-5",
      )}
    >
      {actionButtons.map((button) => (
        <RyeButton
          key={button.text}
          text={button.text}
          vibe={button.vibe}
          variant={button.variant}
          disabled={button.disabled}
          onClick={() => {
            button.action();
            if (button.close ?? true) {
              close();
            }
          }}
        />
      ))}
    </div>
  );
}
